import {
  Alert,
  Anchor,
  Button,
  Card,
  Group,
  List,
  Progress,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Text,
  Title,
} from '@mantine/core';
import { useState } from 'react';
import { Link } from 'react-router';
import type {
  DashboardDonemi,
  DashboardKapsamTipi,
  DashboardKiyas,
  DashboardKpi,
  KpiCarpan,
  KpiOrani,
} from '@isg/validation';
import {
  ISLERIM_HEDEF_HARITASI,
  DASHBOARD_DONEMLERI,
  DASHBOARD_DONEM_ETIKETI,
  KPI_CARPANLARI,
  KPI_CARPAN_ETIKETI,
  TAKVIM_KAYNAK_ETIKETI,
} from '@isg/validation';

import {
  dashboardCiktiYolu,
  useCiktiIndir,
  useDashboardKiyas,
  useDashboardKpi,
  useDashboardOperasyonel,
  useDashboardSecenekleri,
  type DashboardSuzgeci,
} from '../api/queries';
import { oranGoster, sayiGoster, tamSayiGoster, tarihGoster } from '../lib/format';
import { QueryStatus } from './parts';
import { Rozet } from '../sozlesme';
import { ANLAM } from '../theme';

/**
 * ═ DASHBOARD (TUR 12 — Kapsam §26) ════════════════════════════════════════
 *
 * ⚠️ **EKRAN HİÇBİR SAYIYI KENDİ HESAPLAMAZ** (K6). Oran · çalışan başına
 * metrik · kapsam etiketi · dönem etiketi — hepsi SUNUCUDAN gelir. Burada
 * bir `pay / payda` çarpımı yoktur ve olmayacaktır: kuralın üçüncü bir
 * kopyası, sunucudakiyle ayrıştığında **hiç görünmez**.
 *
 * ⚠️ **BOŞ ≠ SIFIR (K12).** `oran === null` bir hesap sonucu değil, bir
 * BİLGİSİZLİKTİR (payda yok) ve **"—"** basılır. Sıfır yazmak, çalışma saati
 * girilmemiş bir işyerini **mükemmel** gösterirdi.
 *
 * ⚠️ **ETİKET SAYININ YANINDA** (K7): aynı ekran iki kişide iki farklı LTIF
 * gösterir ve **ikisi de doğrudur**. Kapsam + dönem + çarpan üçlüsü her
 * zaman görünür — ✏️ 07.10.2026'dan beri **süzgeç kartının denetimlerinde**
 * (seçenek etiketleri sunucudan; gri tekrar şeridi kalktı — ürün sahibi).
 * Çıktı etiketi DEĞİŞMEDİ (K10).
 *
 * ⚠️ **BU SAYFA *"Genel bakış"*IN YERİNE GEÇER** (K11) — menü satırı artmaz;
 * *İşlerim* kendi sayfası olarak kalır.
 */
export function DashboardBloklari(): React.JSX.Element {
  const secenekler = useDashboardSecenekleri();
  const [kapsam, setKapsam] = useState<string>('tumu');
  const [donem, setDonem] = useState<DashboardDonemi>('ytd');
  const [carpan, setCarpan] = useState<KpiCarpan | null>(null);

  const etkinCarpan: KpiCarpan = carpan ?? secenekler.data?.varsayilanCarpan ?? KPI_CARPANLARI[1];
  const [kapsamTipi, kapsamId] = ayirKapsam(kapsam);
  const suzgec: DashboardSuzgeci = { kapsamTipi, kapsamId, donem, carpan: etkinCarpan };
  const kpi = useDashboardKpi(suzgec);
  const indir = useCiktiIndir();

  return (
    <Stack>
      {/* ✏️ 07.10.2026 — BÖLÜM BAŞLIĞI "PERFORMANS" (Genel bakış düzeni): süzgeçler
          İşlerim / Süresi geçenler'in ALTINDA kalır — o kartları SÜZMEZLER
          (yerleşim bilinçli). Başlık, süzgecin neyi süzdüğünü söyler. */}
      <Title order={4} data-testid="dashboard-performans-baslik">
        Performans
      </Title>
      {/* SÜZGEÇLER — ⚠️ ÇARPAN BURADA (K1): tenant ayarı yalnız VARSAYILANI
          verir. Aynı dönem için iki farklı çarpanla iki çıktı EŞZAMANLI
          istenebilsin diye ayar bir kilit değildir.
          ✏️ 07.10.2026: süzgeçler BEYAZ KART içinde; Excel + PDF sağda, ikisi de
          İKİNCİL stil (çerçeveli) — PDF'in koyu dolgusu kalktı. Eski gri
          şerit (*"Holding geneli · Yıl başından bugüne · …"*) KALKTI: taşıdığı
          kapsam · dönem · çarpan zaten süzgeç denetimlerinin kendi değeridir ve
          seçenek etiketleri SUNUCUDAN gelir (K7 — etiket ekranda kurulmaz);
          şeridin tek yeni bilgisi (*n işyeri · m çalışan*) kartın altında küçük
          satırdır. Çıktının kendi kapsam başlığı DEĞİŞMEDİ (K10). */}
      <Card withBorder padding="md" data-testid="dashboard-suzgec">
        <Group align="flex-end" justify="space-between" gap="sm">
          <Group align="flex-end" gap="sm">
            <Select
              label="Kapsam"
              data={(secenekler.data?.kapsamlar ?? []).map((secenek) => ({
                value: birlestirKapsam(secenek.tip, secenek.id),
                label: secenek.etiket,
              }))}
              value={kapsam}
              onChange={(deger) => deger && setKapsam(deger)}
              data-testid="dashboard-kapsam"
              w={280}
              allowDeselect={false}
            />
            <Select
              label="Dönem"
              data={DASHBOARD_DONEMLERI.map((deger) => ({
                value: deger,
                label: DASHBOARD_DONEM_ETIKETI[deger],
              }))}
              value={donem}
              onChange={(deger) => deger && setDonem(deger as DashboardDonemi)}
              data-testid="dashboard-donem"
              w={200}
              allowDeselect={false}
            />
            <Select
              label="Oran çarpanı"
              data={KPI_CARPANLARI.map((deger) => ({
                value: String(deger),
                label: KPI_CARPAN_ETIKETI[deger],
              }))}
              value={String(etkinCarpan)}
              onChange={(deger) => deger && setCarpan(Number(deger) as KpiCarpan)}
              data-testid="dashboard-carpan"
              w={180}
              allowDeselect={false}
            />
          </Group>
          {/* ⚠️ ÇIKTI EKRANDAKİ SÜZGEÇLE (P4 · K10): dosya kapsam · dönem ·
              ÇARPAN üçlüsünü antetine basar — çarpansız basılan bir oran
              tablosu altı ay sonra okunamaz. */}
          <Group gap="sm">
            <Button
              size="xs"
              variant="default"
              data-testid="dashboard-excel"
              onClick={() =>
                indir.mutate({
                  yol: dashboardCiktiYolu(suzgec, 'export'),
                  yedekAd: 'gosterge-paneli.xlsx',
                })
              }
            >
              Excel
            </Button>
            <Button
              size="xs"
              variant="default"
              data-testid="dashboard-pdf"
              onClick={() =>
                indir.mutate({
                  yol: dashboardCiktiYolu(suzgec, 'pdf'),
                  yedekAd: 'gosterge-paneli.pdf',
                })
              }
            >
              PDF
            </Button>
          </Group>
        </Group>
        {kpi.data ? (
          <Text size="xs" c="dimmed" mt="sm" data-testid="dashboard-kapsam-ozeti">
            {kpi.data.isyeriSayisi} işyeri · {kpi.data.calisanSayisi} çalışan
          </Text>
        ) : null}
      </Card>

      {/* ⚠️ **SESSİZ DÜŞMEZ (§2.40).** Bu blok yol testinde bir kez SESSİZCE
          BOŞ KALDI (sunucu 500 veriyordu, ekran hiçbir şey söylemiyordu) ve
          arıza yalnız e2e sayesinde görüldü — kullanıcı elinde *"göstergeler
          çıkmıyor"*tan başka bir cümle olmadan kalırdı. Hata artık ADIYLA
          görünür; süzgeçler yerinde kalır (kullanıcı seçimini kaybetmez). */}
      {kpi.isError ? (
        <Alert color={ANLAM.engel} data-testid="dashboard-hata">
          Göstergeler okunamadı: {kpi.error.message}
        </Alert>
      ) : null}
      {kpi.data ? <KpiBloklari kpi={kpi.data} /> : null}
      <KiyasBlogu suzgec={suzgec} />
      <OperasyonelBlok />
    </Stack>
  );
}

/** Bağ tipi → rota. ⚠️ Eşleme WEB'İN İŞİDİR: `@isg/db` ekran rotası bilmez,
 *  hedef kaydın KİMLİĞİNİ döner. */
function vadeYolu(
  bagli: { tip: 'ekipman' | 'kanaat' | 'yuklenici'; id: string } | null,
): string | null {
  if (bagli === null) return null;
  if (bagli.tip === 'ekipman') return `/ekipman/${bagli.id}`;
  if (bagli.tip === 'kanaat') return `/saglik/${bagli.id}`;
  return `/yukleniciler/${bagli.id}`;
}

function birlestirKapsam(tip: DashboardKapsamTipi, id: string | null): string {
  return id === null ? tip : `${tip}:${id}`;
}

function ayirKapsam(deger: string): [DashboardKapsamTipi, string | null] {
  const [tip, id] = deger.split(':');
  return [(tip ?? 'tumu') as DashboardKapsamTipi, id ?? null];
}

/**
 * ⚠️ **BİÇİM `lib/format.ts`TEN GELİR** (G11'in sayı kardeşi): ekran ne
 * hesaplar ne biçimlendirir. `null` → **"—"** (K12: BOŞ ≠ SIFIR).
 */
function oranMetni(hucre: KpiOrani): string {
  return oranGoster(hucre.oran);
}

/**
 * ⚠️ ÜÇ SAYI (K3): ana işveren · yüklenici · toplam — pay ve payda AYNI
 * eksende ayrışır; yüklenici oranı ana firmanın saatine bölünmez.
 *
 * ✏️ 07.10.2026 — TRIF + LTIF TEK TABLO (Genel bakış düzeni): iki tablo
 * birleşti. **Her oranın yanında KENDİ vaka sayısı durur** — TRIF ve LTIF
 * vakaları farklı kümelerdir (`sira ≥ 2` · `sira ≥ 3`), tek bir *Vaka*
 * sütunu hangisini saydığını söyleyemezdi. Çalışma saati iki oranın ortak
 * paydasıdır ve bir kez yazılır (sunucu aynı `saat` özetini iki orana da
 * verir). Şirket kıyası tablosuyla aynı desen (oran + vaka yan yana).
 */
function OranTablosu({ kpi }: { kpi: DashboardKpi }): React.JSX.Element {
  const satirlar: [string, 'anaIsveren' | 'yuklenici' | 'toplam', string][] = [
    ['Ana işveren', 'anaIsveren', 'ana'],
    ['Yüklenici', 'yuklenici', 'yuklenici'],
    ['Toplam', 'toplam', 'toplam'],
  ];
  return (
    <Card withBorder padding="md" data-testid="kpi-oran-tablosu">
      <Text fw={600}>Kaza oranları — kesimlere göre</Text>
      <Text size="xs" c="dimmed" mb="xs">
        TRIF: tıbbi müdahale ve üstü vakalar · LTIF: kayıp günlü ve üstü vakalar
      </Text>
      <Table>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Kesim</Table.Th>
            <Table.Th>TRIF</Table.Th>
            <Table.Th>Vaka</Table.Th>
            <Table.Th>LTIF</Table.Th>
            <Table.Th>Vaka</Table.Th>
            <Table.Th>Çalışma saati</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {satirlar.map(([etiket, kesim, anahtar]) => {
            const trif: KpiOrani = kpi.trif[kesim];
            const ltif: KpiOrani = kpi.ltif[kesim];
            return (
              <Table.Tr key={anahtar} data-testid={`kpi-oran-${anahtar}`}>
                <Table.Td>{etiket}</Table.Td>
                <Table.Td data-testid={`kpi-trif-${anahtar}-oran`}>{oranMetni(trif)}</Table.Td>
                <Table.Td data-testid={`kpi-trif-${anahtar}-vaka`}>{trif.pay}</Table.Td>
                <Table.Td data-testid={`kpi-ltif-${anahtar}-oran`}>{oranMetni(ltif)}</Table.Td>
                <Table.Td data-testid={`kpi-ltif-${anahtar}-vaka`}>{ltif.pay}</Table.Td>
                <Table.Td data-testid={`kpi-saat-${anahtar}`}>
                  {tamSayiGoster(trif.paydaSaat)}
                </Table.Td>
              </Table.Tr>
            );
          })}
        </Table.Tbody>
      </Table>
    </Card>
  );
}

/**
 * KIYAS (G1.1 — gezinti artçısı 07.09.2026; tur 12'nin "v2" kararı ürün
 * sahibince geri açıldı). Süzgeçle ilişkisi: kıyas **seçili kapsamın alt
 * kırılımını** okur — holding seçiliyse şirketler, şirket seçiliyse
 * işyerleri; ikinci bir kapsam seçici açılmaz. ⚠️ Grafik kütüphanesi kararı
 * ürün sahibinde (G1.2 — yeni bağımlılık); karar gelene dek kıyas sözleşme
 * bileşenleriyle iner: tablo + oransal çubuk (`Progress`). Çubuk payı
 * SUNUCUDAN gelir (`cubuk`) — ekran hiçbir sayıyı kendi hesaplamaz (K6).
 * ~~Renk ANLAM token'ından: en yüksek oran `dikkat`, diğerleri `bilgi`.~~
 * ✏️ 07.10.2026: çubuklar TEK renk — platform aksanı (birincil renk). Durum
 * rengi YOK: en yüksek oranı turuncuya (ya da hedefi aşanı kırmızıya) boyamak
 * bir KPI hedef tanımı ister ve o tanım v1'de yoktur (Kapsam §16.8.1 açık ucu
 * — Faz 5). Başlığın sağındaki tekrar eden kapsam özeti de kalktı: aynı üçlü
 * süzgeç kartında durur.
 */
function KiyasBlogu({ suzgec }: { suzgec: DashboardSuzgeci }): React.JSX.Element {
  const kiyas = useDashboardKiyas(suzgec);
  if (kiyas.isError) {
    return (
      <Alert color={ANLAM.engel} data-testid="dashboard-kiyas-hata">
        Kıyas okunamadı: {kiyas.error.message}
      </Alert>
    );
  }
  if (!kiyas.data) return <QueryStatus queries={[kiyas]} />;
  const veri = kiyas.data;
  return (
    <Card withBorder padding="md" data-testid="dashboard-kiyas">
      <Text fw={600} mb="xs">
        Kaza oranı kıyası — {veri.kirilimEtiketi}
      </Text>
      {veri.satirlar.length <= 1 && (
        <Text size="xs" c="dimmed" data-testid="dashboard-kiyas-tek">
          Kıyas için üst kapsamı seçin (şirket ya da tüm kapsam).
        </Text>
      )}
      <Table>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Birim</Table.Th>
            <Table.Th>İşyeri</Table.Th>
            <Table.Th w="30%">TRIF</Table.Th>
            <Table.Th w="30%">LTIF</Table.Th>
            <Table.Th>Çalışma saati</Table.Th>
            <Table.Th>Kaza</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {veri.satirlar.map((satir) => (
            <Table.Tr key={satir.id} data-testid={`dashboard-kiyas-satir-${satir.id}`}>
              <Table.Td>{satir.etiket}</Table.Td>
              <Table.Td>{satir.isyeriSayisi}</Table.Td>
              <Table.Td>
                <KiyasCubugu hucre={satir.trif} testId={`dashboard-kiyas-${satir.id}-trif`} />
              </Table.Td>
              <Table.Td>
                <KiyasCubugu hucre={satir.ltif} testId={`dashboard-kiyas-${satir.id}-ltif`} />
              </Table.Td>
              <Table.Td>{tamSayiGoster(satir.trif.paydaSaat)}</Table.Td>
              <Table.Td>{satir.kazaSayisi}</Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Card>
  );
}

/** Oran + oransal çubuk; oran boşsa "—" ve çubuk YOK (K12: BOŞ ≠ SIFIR). */
function KiyasCubugu({
  hucre,
  testId,
}: {
  hucre: DashboardKiyas['satirlar'][number]['trif'];
  testId: string;
}): React.JSX.Element {
  return (
    <Group gap="xs" wrap="nowrap">
      <Text size="sm" w={56} data-testid={`${testId}-oran`}>
        {oranGoster(hucre.oran)}
      </Text>
      {hucre.cubuk !== null && (
        // Renk verilmez: Mantine birincil rengi (platform aksanı) okur — tek renk,
        // durum yok (yukarıdaki not).
        <Progress
          value={hucre.cubuk * 100}
          size="md"
          style={{ flex: 1 }}
          aria-label={`${hucre.pay} vaka`}
          data-testid={`${testId}-cubuk`}
        />
      )}
      <Text size="xs" c="dimmed">
        {hucre.pay} vaka
      </Text>
    </Group>
  );
}

/**
 * ✏️ 07.10.2026 — BÜYÜK RAKAMLI KART (Genel bakış düzeni): tek büyük, kalın
 * sayı + altında bağlam satırı. **Değerlendirme etiketi YOK** (*"Mükemmel"* ·
 * *"Geliştirilmeli"* yasak — sayının iyi mi kötü mü olduğunu söylemek bir
 * KPI hedef tanımı ister, o tanım v1'de yoktur). Punto tema başlık
 * ölçeğinden (`fz="h1"`) — ekranda boyut yazılmaz (G5).
 */
function BuyukRakamKarti({
  baslik,
  deger,
  baglam,
  altSatir,
  testId,
}: {
  baslik: string;
  deger: string;
  baglam: string;
  /** İkinci bağlam satırı — yalnız söyleyecek bir şey varsa (null → çizilmez). */
  altSatir?: string | null;
  testId: string;
}): React.JSX.Element {
  return (
    <Card withBorder padding="md" data-testid={testId}>
      <Text size="sm" fw={600}>
        {baslik}
      </Text>
      <Text component="p" fz="h1" fw={700} lh={1.1} my="xs" data-testid={`${testId}-deger`}>
        {deger}
      </Text>
      <Text size="xs" c="dimmed" data-testid={`${testId}-baglam`}>
        {baglam}
      </Text>
      {altSatir ? (
        <Text size="xs" c={ANLAM.dikkat} data-testid={`${testId}-alt-satir`}>
          {altSatir}
        </Text>
      ) : null}
    </Card>
  );
}

function KpiBloklari({ kpi }: { kpi: DashboardKpi }): React.JSX.Element {
  return (
    <Stack>
      {/* ✏️ 07.10.2026 — BEŞ BÜYÜK RAKAMLI KART, bu sırayla: TRIF · LTIF · Kayıp
          gün · Tehlike bildirimi · Eğitim. TRIF/LTIF kartı TOPLAM kesimi taşır
          (üç sayının ayrımı aşağıdaki tabloda); bağlam satırı `{vaka} vaka ·
          {saat} saat`. Oran gösterimi DEĞİŞMEDİ (*"100 çalışan başına"* önerisi
          reddedildi — çarpan süzgeçte durur, K1). BOŞ ≠ SIFIR aynen (K12). */}
      <SimpleGrid cols={{ base: 1, md: 3, lg: 5 }} data-testid="kpi-kartlar">
        <BuyukRakamKarti
          baslik="TRIF — kaydedilebilir kaza oranı"
          deger={oranMetni(kpi.trif.toplam)}
          baglam={`${kpi.trif.toplam.pay} vaka · ${tamSayiGoster(kpi.trif.toplam.paydaSaat)} saat`}
          testId="kpi-trif-kart"
        />
        <BuyukRakamKarti
          baslik="LTIF — kayıp günlü kaza oranı"
          deger={oranMetni(kpi.ltif.toplam)}
          baglam={`${kpi.ltif.toplam.pay} vaka · ${tamSayiGoster(kpi.ltif.toplam.paydaSaat)} saat`}
          testId="kpi-ltif-kart"
        />
        {/* Madde 309-c (08.10.2026): ölümlü vakadan SÖZ EDİLMEZ; kayıp günlü
            basamakta (3–4) `kayip_gun` boşsa alt satır — sayı SUNUCUDAN
            (`kayipGunEksikMi` yüklemi), ölümlü NULL sayıya girmez. */}
        <BuyukRakamKarti
          baslik="Kayıp gün"
          deger={String(kpi.kayipGun.toplam)}
          baglam={`${kpi.kazaSayisi} kaza kaydı · ana işveren ${kpi.kayipGun.anaIsveren} · yüklenici ${kpi.kayipGun.yuklenici}`}
          altSatir={
            kpi.kayipGun.eksikVaka > 0
              ? `${kpi.kayipGun.eksikVaka} vakada kayıp gün girilmemiş`
              : null
          }
          testId="kpi-kayip-gun"
        />
        {/* ⚠️ ADI "TEHLİKE BİLDİRİMİ"DİR (K5): kaza SAYILMAZ ve kaynak
            kırılımı görünür kalır (madde 153). */}
        <BuyukRakamKarti
          baslik="Çalışan başına tehlike bildirimi"
          deger={sayiGoster(kpi.tehlikeBildirimi.calisanBasina, 2)}
          baglam={`${kpi.tehlikeBildirimi.olay} ramak kala · ${kpi.tehlikeBildirimi.uygunsuzluk} uygunsuzluk (kaza sayılmaz)`}
          testId="kpi-tehlike-bildirimi"
        />
        {/* ⚠️ BİRİM DERS SAATİDİR (K16 / madde 173) — yalnız "saat" demek
            bugün YALAN olur. */}
        <BuyukRakamKarti
          baslik="Çalışan başına eğitim ders saati"
          deger={sayiGoster(kpi.egitim.calisanBasina, 2)}
          baglam={`Toplam ${sayiGoster(kpi.egitim.dersSaati)} ders saati · yalnız ana işveren`}
          testId="kpi-egitim"
        />
      </SimpleGrid>

      <OranTablosu kpi={kpi} />
    </Stack>
  );
}

/**
 * (B) OPERASYONEL BLOK — ⚠️ **KAPSAMI BAHÇEDİR, (A)'nın süzgeci DEĞİL**
 * (§26.6): (A) bir ölçümdür ve kapsam seçilir; (B) bir iş listesidir ve
 * cevabı bahçedir. Listedeki kalemler **bildirimin taradığı kalemlerin ta
 * kendisidir** — kullanıcı e-postada gördüğünü burada da görür.
 */
function OperasyonelBlok(): React.JSX.Element | null {
  const veri = useDashboardOperasyonel();
  if (veri.isError || !veri.data) return null;
  return (
    <Stack>
      <Group gap="xs">
        <Title order={4}>Açık kalemler</Title>
        <Text size="sm" c="dimmed" data-testid="operasyonel-kapsam">
          {veri.data.kapsamEtiketi}
        </Text>
      </Group>

      <SimpleGrid cols={{ base: 1, md: 2 }}>
        {veri.data.vadeler.map((konu) => (
          <Card withBorder padding="md" key={konu.konu} data-testid={`vade-${konu.konu}`}>
            <Group justify="space-between" mb="xs">
              <Text fw={600}>{konu.etiket}</Text>
              <Group gap={4}>
                <Rozet anlam="bilgi" testId={`vade-${konu.konu}-toplam`}>
                  {String(konu.toplam)}
                </Rozet>
                {konu.gecmis > 0 ? (
                  <Rozet anlam="gecikmis" testId={`vade-${konu.konu}-gecmis`}>
                    {`${konu.gecmis} geçti`}
                  </Rozet>
                ) : null}
              </Group>
            </Group>
            {konu.satirlar.length === 0 ? (
              <Text size="sm" c="dimmed">
                Yaklaşan kalem yok.
              </Text>
            ) : (
              <List size="sm" data-testid={`vade-${konu.konu}-liste`}>
                {konu.satirlar.map((satir) => {
                  const yol = vadeYolu(satir.bagli);
                  const metin = `${satir.baslik} — ${tarihGoster(satir.bitis)}${
                    satir.gecti ? ' (geçti)' : ''
                  }`;
                  return (
                    <List.Item
                      key={`${satir.konu}-${satir.kaynakRef ?? satir.baslik}`}
                      data-testid="vade-satiri"
                    >
                      {/* ⚠️ ÖLÜ BAĞ ÇİZİLMEZ (§2.40): tekil ekranı olmayan
                          kalem DÜZ METİN kalır — tıklanıp hiçbir şey
                          olmayan bir bağ, hata veren bir düğmeden yalnız
                          bir tık daha sessizdir. */}
                      {yol === null ? (
                        metin
                      ) : (
                        <Anchor component={Link} to={yol} size="sm">
                          {metin}
                        </Anchor>
                      )}
                    </List.Item>
                  );
                })}
              </List>
            )}
          </Card>
        ))}
      </SimpleGrid>

      {/* ⚠️ TAKVİM: HER SATIR KAYNAĞINI TAŞIR (K9) — üçüncü bir takvim
          kavramı açılmadı; yükümlülük vadeleri tur 8 / C1'den beri GÖREV
          olarak doğuyor ve aynı listede ayırt edilebilir duruyor. */}
      <Card withBorder padding="md" data-testid="dashboard-takvim">
        <Group justify="space-between" mb="xs">
          {/* ⚠️ GEÇMİŞ VADELER BURADA DEĞİL, *"Süresi geçenler"* bloğunda
              (T8): iki blok aynı soruya iki farklı sayıyla cevap vermesin. */}
          <Text fw={600}>Takvim — önümüzdeki 60 gün</Text>
          <Anchor component={Link} to="/ekipman/gorevler" size="sm">
            Görev listesine git →
          </Anchor>
        </Group>
        {veri.data.takvim.length === 0 ? (
          <Text size="sm" c="dimmed">
            Bu pencerede açık görev yok.
          </Text>
        ) : (
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Vade</Table.Th>
                <Table.Th>Kalem</Table.Th>
                <Table.Th>Nesne</Table.Th>
                <Table.Th>Kaynak</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {veri.data.takvim.map((satir) => (
                <Table.Tr key={satir.occurrenceId} data-testid="takvim-satiri">
                  <Table.Td>{tarihGoster(satir.vadeTarihi)}</Table.Td>
                  <Table.Td>
                    {/* ✏️ KA-10 (madde 262): satır görev kaydına gider — yol K-103b
                        haritasından (`ISLERIM_HEDEF_HARITASI.gorev`), yeni desen yok. */}
                    <Anchor
                      component={Link}
                      to={ISLERIM_HEDEF_HARITASI.gorev(satir.occurrenceId)}
                      size="sm"
                      data-testid="takvim-bagi"
                    >
                      {satir.kalemAd}
                    </Anchor>
                  </Table.Td>
                  <Table.Td>
                    {satir.nesneAd} · {satir.workplaceAd}
                  </Table.Td>
                  <Table.Td data-testid="takvim-kaynak">
                    {TAKVIM_KAYNAK_ETIKETI[satir.kaynak]}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Card>
    </Stack>
  );
}
